import React, { useState, useEffect, useCallback } from 'react';
import PickupScheduleCard from './pp2/PickupScheduleCard';
import PackOrderCard from './pp2/PackOrderCard';
import { CARRIER_NAME } from './carriers';
import { fmtWh } from './time';
import { authFetch, API } from './api';
import CarrierServicesTab from './CarrierServicesTab';

interface Webshop { id: string; name: string; platform: string; }
interface Sender {
  id?: number; webshop_id: string; quick_id?: string; name: string;
  vat_number?: string; eori_number?: string;
  is_sender?: boolean; is_receiver?: boolean; is_system_integration?: boolean;
  country?: string; care_of?: string; address1?: string; address2?: string;
  postal_code?: string; city?: string; state?: string; residential?: boolean;
  contact_name?: string; phone?: string; fax?: string; email?: string;
  sms_number?: string; door_code?: string;
  sender_reference?: string; receiver_reference?: string; cod_reference?: string;
  delivery_instruction_1?: string; delivery_instruction_2?: string;
  delivery_instruction_3?: string; delivery_instruction_4?: string;
  private_route?: string;
  category_1?: string; category_2?: string; category_3?: string;
  category_4?: number; category_5?: number; category_6?: number;
}
interface CarrierAccount {
  id?: number; webshop_id: string; carrier_code: string; label: string;
  credentials: Record<string, string>; credentials_masked?: Record<string, string>;
  is_active?: boolean;
}
interface ServiceCode {
  id: number; carrier_code?: string; code: string;   // org-level rows (carrier_code, not per-account)
  name?: string; description?: string; is_active: boolean;
}
interface ShippingMethod {
  id: number; webshop_id: string; name: string; external_id: string;
  is_active: boolean; config_id?: number;
  carrier_account_id?: number; service_code?: string; fallback_carrier_id?: number;
  sender_id?: number; customer_type?: string;
  pickup_point_required?: boolean; home_delivery?: boolean;
  countries?: string[]; default_weight_kg?: number;
  free_over_threshold?: boolean; standard_rate?: number | null;
  promises?: Promise_[];
}
/** What the store's terms promise the customer for this method, in WORKING DAYS, per
 *  destination country (Fernando, 2026-10-07). Read by the parcel statistics. */
interface Promise_ {
  country: string; days_min: number; days_max: number; note?: string | null;
  source_url?: string | null; checked_at?: string | null; updated_by?: string | null;
}
const promiseText = (p: Promise_) => p.days_min === p.days_max ? `${p.days_min}` : `${p.days_min}–${p.days_max}`;

// `pill` = what the warehouse calls the carrier on the Orders page (the filter
// pill and the grid badge). Same words as backend CARRIER_DISPLAY; a method's pill
// is derived from its carrier account, never typed (Fernando, 2026-09-15). The words
// come from the one frontend copy (carriers.tsx); `label` names the ACCOUNT.
const CARRIER_CODES = [
  { value: 'dpd_baltics', label: 'DPD Baltics',           pill: CARRIER_NAME.dpd_baltics },
  { value: 'posti',       label: 'Posti (OmaPosti Pro)',  pill: CARRIER_NAME.posti },
  { value: 'omniva',      label: 'Omniva',                pill: CARRIER_NAME.omniva },
  { value: 'packeta',     label: 'Packeta',               pill: CARRIER_NAME.packeta },
  { value: 'bring',       label: 'Bring',                 pill: CARRIER_NAME.bring },
];
const CARRIER_CRED_FIELDS: Record<string, { key: string; label: string; secret?: boolean }[]> = {
  dpd_baltics: [
    { key: 'bearer_token',         label: 'Bearer Token (from telli.dpd.ee → Token list)', secret: true },
    { key: 'sender_phone_fallback', label: 'Sender Phone (e.g. +3725XXXXXXX) — used if sender has no phone' },
    { key: 'username',             label: 'Username (for token creation only)' },
    { key: 'password',             label: 'Password (for token creation only)', secret: true },
    { key: 'customer_number',      label: 'Customer Number' },
    { key: 'country_code',         label: 'Country Code (EE / LV / LT)' },
  ],
  posti: [
    { key: 'api_key',         label: 'Label API Key (OmaPosti Pro)', secret: true },
    { key: 'customer_number', label: 'Customer / Contract Number' },
    { key: 'label_media',     label: 'Label Media (thermo-225 / laser-a5)' },
    { key: 'pickup_client_id',     label: 'Pickup Point API — Client ID (developer.posti.com)' },
    { key: 'pickup_client_secret', label: 'Pickup Point API — Client Secret', secret: true },
    // Tracking API: the Posti SERVICE ACCOUNT with the Tracking API role (ma_…_1P, live 2026-10-02 —
    // the _2P "e-commerce plugin" pair signs in but is refused every lookup). Test with the button below.
    { key: 'tracking_account', label: 'Tracking API — service account name (ma_…_1P)' },
    { key: 'tracking_secret',  label: 'Tracking API — service account secret', secret: true },
  ],
  omniva: [
    { key: 'username', label: 'API Username (partner code)' },
    { key: 'password', label: 'API Password', secret: true },
    { key: 'customer_code', label: 'Customer code (leave blank = username)' },
    { key: 'test_mode', label: 'Test mode (true = test-omx environment)' },
  ],
  // the fields the backend actually reads (carriers._packeta_create_label, the service sync):
  // the form offered api_key / sender_id, which nothing uses (security review 2026-09-23)
  packeta: [
    { key: 'api_password', label: 'API Password (client.packeta.com → Client support)', secret: true },
    { key: 'feed_key', label: 'Feed / API Key (branch and carrier feeds)', secret: true },
    { key: 'customer_number', label: 'Customer Number' },
    { key: 'test_mode', label: 'Test mode (true = test environment)' },
  ],
  bring: [
    { key: 'api_uid', label: 'Mybring User ID (login email)' },
    { key: 'api_key', label: 'API Key', secret: true },
    { key: 'customer_number', label: 'Customer Number' },
    { key: 'test_mode', label: 'Test mode (true = validate only, no real shipment)' },
  ],
};
// Fallback only. The live list comes from /api/shipping/carrier-countries, which
// is written by the pickup-point syncs — see backend/schema/migrations/003.
const COUNTRIES  = ['FI','EE','LV','LT','SE','NO','DE','PL','DK'];

function Inp({ label, value, onChange, secret }: {
  label: string; value: string; onChange: (v: string) => void; secret?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input type={secret ? 'password' : 'text'} value={value} onChange={e => onChange(e.target.value)}
        className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
    </div>
  );
}
function Chk({ label, checked, onChange }: {
  label: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="w-4 h-4 accent-blue-500" />
      {label}
    </label>
  );
}
function Sec({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-gray-800 rounded-xl overflow-x-auto mb-3">
      <div className="bg-[#1a1d27] px-4 py-2 text-xs text-gray-500 uppercase tracking-wider font-semibold">{title}</div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}
function Err({ msg }: { msg: string }) {
  return msg ? <div className="mb-3 text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-2">{msg}</div> : null;
}

function blankSender(webshop_id: string): Sender {
  return { webshop_id, name: '', quick_id: '', vat_number: '', eori_number: '',
    is_sender: true, is_receiver: false, is_system_integration: false,
    country: '', care_of: '', address1: '', address2: '', postal_code: '', city: '',
    state: '', residential: false, contact_name: '', phone: '', fax: '', email: '',
    sms_number: '', door_code: '', sender_reference: '', receiver_reference: '',
    cod_reference: '', delivery_instruction_1: '', delivery_instruction_2: '',
    delivery_instruction_3: '', delivery_instruction_4: '', private_route: '',
    category_1: '', category_2: '', category_3: '', category_4: 0, category_5: 0, category_6: 0 };
}

function SenderForm({ initial, webshop_id, onSave, onCancel, error, saving }: {
  initial?: Sender; webshop_id: string; onSave: (s: Sender) => void; onCancel: () => void;
  error?: string; saving?: boolean;
}) {
  const [s, setS] = useState<Sender>(initial ?? blankSender(webshop_id));
  const f = (k: keyof Sender) => (v: string | boolean | number) => setS(p => ({ ...p, [k]: v }));
  return (
    <div className="space-y-1">
      <Err msg={error ?? ''} />
      <Sec title="Basic Information">
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Quick ID" value={s.quick_id??''} onChange={f('quick_id')} />
          <Inp label="Name *"   value={s.name}         onChange={f('name')} />
          <Inp label="VAT Number"  value={s.vat_number??''}  onChange={f('vat_number')} />
          <Inp label="EORI Number" value={s.eori_number??''} onChange={f('eori_number')} />
        </div>
        <div className="flex gap-4 pt-1">
          <Chk label="Sender"             checked={!!s.is_sender}             onChange={f('is_sender')} />
          <Chk label="Receiver"           checked={!!s.is_receiver}           onChange={f('is_receiver')} />
          <Chk label="System Integration" checked={!!s.is_system_integration} onChange={f('is_system_integration')} />
        </div>
      </Sec>
      <Sec title="Address Details">
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Country"       value={s.country??''}     onChange={f('country')} />
          <Inp label="Care Of (C/O)" value={s.care_of??''}    onChange={f('care_of')} />
          <Inp label="Address 1"     value={s.address1??''}    onChange={f('address1')} />
          <Inp label="Address 2"     value={s.address2??''}    onChange={f('address2')} />
          <Inp label="Postal Code"   value={s.postal_code??''} onChange={f('postal_code')} />
          <Inp label="City"          value={s.city??''}        onChange={f('city')} />
          <Inp label="State / Region" value={s.state??''}     onChange={f('state')} />
        </div>
        <Chk label="Residential" checked={!!s.residential} onChange={f('residential')} />
      </Sec>
      <Sec title="Contact Information">
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Contact"    value={s.contact_name??''} onChange={f('contact_name')} />
          <Inp label="Phone"      value={s.phone??''}        onChange={f('phone')} />
          <Inp label="Fax"        value={s.fax??''}          onChange={f('fax')} />
          <Inp label="Email"      value={s.email??''}        onChange={f('email')} />
          <Inp label="SMS Number" value={s.sms_number??''}   onChange={f('sms_number')} />
          <Inp label="Door Code"  value={s.door_code??''}    onChange={f('door_code')} />
        </div>
      </Sec>
      <Sec title="Default Values">
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Sender Reference"       value={s.sender_reference??''}       onChange={f('sender_reference')} />
          <Inp label="Receiver Reference"     value={s.receiver_reference??''}     onChange={f('receiver_reference')} />
          <Inp label="C.O.D Reference"        value={s.cod_reference??''}          onChange={f('cod_reference')} />
          <Inp label="Delivery Instruction 1" value={s.delivery_instruction_1??''} onChange={f('delivery_instruction_1')} />
          <Inp label="Delivery Instruction 2" value={s.delivery_instruction_2??''} onChange={f('delivery_instruction_2')} />
          <Inp label="Delivery Instruction 3" value={s.delivery_instruction_3??''} onChange={f('delivery_instruction_3')} />
          <Inp label="Delivery Instruction 4" value={s.delivery_instruction_4??''} onChange={f('delivery_instruction_4')} />
          <Inp label="Private Route"          value={s.private_route??''}          onChange={f('private_route')} />
        </div>
      </Sec>
      <Sec title="Search Information">
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Category 1 (Text)"    value={s.category_1??''}        onChange={f('category_1')} />
          <Inp label="Category 4 (Integer)" value={String(s.category_4??0)} onChange={v => f('category_4')(parseInt(v)||0)} />
          <Inp label="Category 2 (Text)"    value={s.category_2??''}        onChange={f('category_2')} />
          <Inp label="Category 5 (Integer)" value={String(s.category_5??0)} onChange={v => f('category_5')(parseInt(v)||0)} />
          <Inp label="Category 3 (Text)"    value={s.category_3??''}        onChange={f('category_3')} />
          <Inp label="Category 6 (Integer)" value={String(s.category_6??0)} onChange={v => f('category_6')(parseInt(v)||0)} />
        </div>
      </Sec>
      <div className="flex gap-2 pt-2">
        <button onClick={() => onSave(s)} disabled={saving}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
          {saving ? 'Saving…' : 'Save sender'}
        </button>
        <button onClick={onCancel}        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg">Cancel</button>
      </div>
    </div>
  );
}

function SendersTab({ webshop_id }: { webshop_id: string }) {
  const [senders, setSenders]   = useState<Sender[]>([]);
  const [editing, setEditing]   = useState<Sender|null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError]       = useState('');
  const [saving,  setSaving]    = useState(false);
  const load = useCallback(async () => {
    const r = await authFetch(`${API}/shipping/senders?webshop_id=${webshop_id}`);
    if (r.ok) setSenders(await r.json());
  }, [webshop_id]);
  useEffect(() => { load(); }, [load]);

  const save = async (s: Sender) => {
    setError(''); setSaving(true);
    try {
      const r = s.id
        ? await authFetch(`${API}/shipping/senders/${s.id}`, { method:'PUT',  body:JSON.stringify(s) })
        : await authFetch(`${API}/shipping/senders`,          { method:'POST', body:JSON.stringify(s) });
      if (r.ok) { setEditing(null); setCreating(false); setSaving(false); load(); }
      else { const d = await r.json().catch(()=>({})); setError((d as any).detail||'Save failed'); setSaving(false); }
    } catch (e: any) {
      setError(e?.message || 'Network error — could not save sender');
      setSaving(false);
    }
  };
  const del = async (id: number) => {
    if (!window.confirm('Delete this sender?')) return;
    await authFetch(`${API}/shipping/senders/${id}`, { method:'DELETE' }); load();
  };
  if (creating) return <SenderForm webshop_id={webshop_id} onSave={save} onCancel={() => setCreating(false)} error={error} saving={saving} />;
  if (editing)  return <SenderForm initial={editing} webshop_id={webshop_id} onSave={save} onCancel={() => setEditing(null)} error={error} saving={saving} />;
  return (
    <div>
      <Err msg={error} />
      <div className="space-y-2 mb-4">
        {senders.length===0 && <p className="text-gray-600 text-sm py-4">No senders yet.</p>}
        {senders.map(s => (
          <div key={s.id} className="bg-[#1a1d27] border border-gray-800 rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="flex-1">
              <div className="text-white font-medium text-sm">{s.name}</div>
              <div className="text-gray-500 text-xs">{[s.address1,s.city,s.country].filter(Boolean).join(', ')}</div>
            </div>
            <div className="flex gap-1">
              {s.is_sender   && <span className="text-xs bg-blue-900/40  text-blue-400  px-2 py-0.5 rounded">Sender</span>}
              {s.is_receiver && <span className="text-xs bg-green-900/40 text-green-400 px-2 py-0.5 rounded">Receiver</span>}
            </div>
            <button onClick={() => setEditing(s)} className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg">Edit</button>
            <button onClick={() => del(s.id!)}    className="px-2 py-1 text-xs bg-red-900/40 hover:bg-red-800/60 text-red-400 rounded-lg">Delete</button>
          </div>
        ))}
      </div>
      <button onClick={() => setCreating(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
        <span className="text-lg leading-none">+</span> New sender
      </button>
    </div>
  );
}

interface OrgCarrier { id: number; carrier_code: string; label: string; is_active: boolean; }

function CarriersTab({ webshop_id }: { webshop_id: string }) {
  const [carriers, setCarriers] = useState<CarrierAccount[]>([]);
  const [orgCarriers, setOrgCarriers] = useState<OrgCarrier[]>([]);
  const [open, setOpen]         = useState(false);
  const [editing, setEditing]   = useState<CarrierAccount|null>(null);
  const [form, setForm]         = useState<CarrierAccount>({ webshop_id, carrier_code:'dpd_baltics', label:'', credentials:{} });
  const [useOrg, setUseOrg]     = useState(true);
  const [orgId, setOrgId]       = useState<number | ''>('');
  const [error, setError]       = useState('');
  const [seeding, setSeeding]   = useState<number|null>(null);
  const load = useCallback(async () => {
    const r = await authFetch(`${API}/shipping/carriers?webshop_id=${webshop_id}`);
    if (r.ok) setCarriers(await r.json());
    const o = await authFetch(`${API}/shipping/org-carriers`);
    if (o.ok) setOrgCarriers(await o.json());
  }, [webshop_id]);
  useEffect(() => { load(); }, [load]);
  const startNew  = () => { setEditing(null); setForm({ webshop_id, carrier_code:'dpd_baltics', label:'', credentials:{} }); setUseOrg(true); setOrgId(''); setOpen(true); };
  const startEdit = (c: CarrierAccount) => { setEditing(c); setForm({ ...c, credentials:{ ...(c.credentials_masked??{}) } }); setUseOrg(false); setOrgId(''); setOpen(true); };
  const save = async () => {
    setError('');
    const body: any = { ...form };
    // linking: a new account, or an existing one that still has its own credentials (2026-10-01)
    const linkable = !editing || !(editing as any).org_account_id;
    if (linkable && useOrg) {
      if (!orgId) { setError('Choose a WMS contract, or switch to own credentials'); return; }
      const org = orgCarriers.find(o => o.id === orgId);
      if (editing && !window.confirm(`Move "${editing.label}" onto the WMS contract "${org?.label}"? `
          + 'Its own credentials are removed; every label, pickup sync and tracking call then uses the contract.')) return;
      body.org_account_id = orgId;
      body.carrier_code = org?.carrier_code || body.carrier_code;
      body.credentials = {};
    }
    const r = editing?.id
      ? await authFetch(`${API}/shipping/carriers/${editing.id}`, { method:'PUT',  body:JSON.stringify(body) })
      : await authFetch(`${API}/shipping/carriers`,                { method:'POST', body:JSON.stringify(body) });
    if (r.ok) { setOpen(false); setEditing(null); load(); }
    else { const d = await r.json().catch(()=>({})); setError((d as any).detail||'Save failed'); }
  };
  const del  = async (id: number) => {
    if (!window.confirm('Delete this carrier?')) return;
    await authFetch(`${API}/shipping/carriers/${id}`, { method:'DELETE' }); load();
  };
  const seed = async (id: number) => {
    setSeeding(id);
    await authFetch(`${API}/shipping/carriers/${id}/seed-service-codes`, { method:'POST' });
    setSeeding(null);
  };
  const credFields = CARRIER_CRED_FIELDS[form.carrier_code] ?? [];
  return (
    <div>
      <Err msg={error} />
      {open && (
        <div className="bg-[#1a1d27] border border-gray-700 rounded-xl p-4 mb-4 space-y-3">
          <p className="text-white font-medium text-sm">{editing ? 'Edit carrier' : 'New carrier'}</p>
          {(!editing || !(editing as any).org_account_id) && (
            <div className="flex gap-4 text-sm text-gray-300">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={useOrg} onChange={() => setUseOrg(true)} className="accent-blue-500" />
                Use a WMS carrier contract
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={!useOrg} onChange={() => setUseOrg(false)} className="accent-blue-500" />
                Store's own credentials
              </label>
            </div>
          )}
          {(!editing || !(editing as any).org_account_id) && useOrg ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">WMS contract</label>
                <select value={orgId} onChange={e => setOrgId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none">
                  <option value="">— choose contract —</option>
                  {orgCarriers.filter(o => o.is_active && (!editing || o.carrier_code === form.carrier_code)).map(o => (
                    <option key={o.id} value={o.id}>{o.label} ({CARRIER_CODES.find(x => x.value===o.carrier_code)?.label ?? o.carrier_code})</option>
                  ))}
                </select>
              </div>
              <Inp label="Display label" value={form.label} onChange={v => setForm(p => ({ ...p, label:v }))} />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Carrier type</label>
                  <select value={form.carrier_code} onChange={e => setForm(p => ({ ...p, carrier_code:e.target.value, credentials:{} }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none">
                    {CARRIER_CODES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <Inp label="Display label" value={form.label} onChange={v => setForm(p => ({ ...p, label:v }))} />
              </div>
              {credFields.map(cf => (
                <Inp key={cf.key} label={cf.label} secret={cf.secret}
                  value={form.credentials[cf.key]??''}
                  onChange={v => setForm(p => ({ ...p, credentials:{ ...p.credentials, [cf.key]:v } }))} />
              ))}
            </>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={save} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">Save</button>
            <button onClick={() => { setOpen(false); setEditing(null); }} className="px-4 py-2 bg-gray-700 text-gray-300 text-sm rounded-lg">Cancel</button>
          </div>
        </div>
      )}
      <div className="space-y-2 mb-4">
        {carriers.length===0 && !open && <p className="text-gray-600 text-sm py-4">No carriers configured yet.</p>}
        {carriers.map(c => (
          <div key={c.id} className="bg-[#1a1d27] border border-gray-800 rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="flex-1">
              <div className="text-white font-medium text-sm">{c.label}</div>
              <div className="text-gray-500 text-xs">{CARRIER_CODES.find(x => x.value===c.carrier_code)?.label ?? c.carrier_code}</div>
            </div>
            {(c as any).org_label ? (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-900/40 text-blue-300" title="Credentials managed centrally — rotating the WMS contract updates every linked store">
                WMS contract: {(c as any).org_label}
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400" title="This store uses its own carrier credentials">
                own credentials
              </span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded-full ${c.is_active ? 'bg-green-900/40 text-green-400' : 'bg-gray-800 text-gray-500'}`}>
              {c.is_active ? 'Active' : 'Inactive'}
            </span>
            <button onClick={() => seed(c.id!)} disabled={seeding===c.id}
              className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg disabled:opacity-40">
              {seeding===c.id ? '…' : 'Sync codes'}
            </button>
            <button onClick={() => startEdit(c)} className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg">Edit</button>
            <button onClick={() => del(c.id!)}   className="px-2 py-1 text-xs bg-red-900/40 hover:bg-red-800/60 text-red-400 rounded-lg">Delete</button>
          </div>
        ))}
      </div>
      {!open && (
        <button onClick={startNew} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
          <span className="text-lg leading-none">+</span> New carrier
        </button>
      )}
    </div>
  );
}

// Store-level view: READ-ONLY list of the codes this store can use (= the ones
// activated on the WMS contract). Activation itself lives in the WMS carrier
// contracts panel — the ceiling is set once at WMS level and every store draws
// from it. See docs/PRINCIPLES.md § 1-2.
function ServiceCodesTab({ webshop_id }: { webshop_id: string }) {
  const [carriers,   setCarriers]   = useState<CarrierAccount[]>([]);
  const [selCarrier, setSelCarrier] = useState<number|''>('');
  const [codes,      setCodes]      = useState<ServiceCode[]>([]);
  useEffect(() => {
    authFetch(`${API}/shipping/carriers?webshop_id=${webshop_id}`).then(r => r.ok ? r.json() : []).then(setCarriers);
  }, [webshop_id]);
  const loadCodes = useCallback(async (cid: number) => {
    const r = await authFetch(`${API}/shipping/service-codes?carrier_account_id=${cid}`);
    if (r.ok) setCodes(await r.json());
  }, []);
  useEffect(() => { if (selCarrier) loadCodes(selCarrier as number); }, [selCarrier, loadCodes]);
  return (
    <div>
      <p className="text-gray-500 text-sm mb-4">
        The service codes this store can use — i.e. the ones activated on the WMS contract.
        To activate/deactivate codes, use the <b>WMS carrier contracts</b> panel at the top
        of Shipping Settings (Service codes button on each contract).
      </p>
      <div className="mb-4">
        <label className="block text-xs text-gray-500 mb-1">Carrier</label>
        <select value={selCarrier} onChange={e => setSelCarrier(Number(e.target.value)||'')}
          className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none w-72">
          <option value="">Select carrier…</option>
          {carriers.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      {selCarrier !== '' && (
        <div className="bg-[#1a1d27] border border-gray-800 rounded-xl overflow-x-auto mb-4">
          <div className="grid grid-cols-[100px_1fr_2fr] min-w-[360px] px-4 py-2 border-b border-gray-800 text-xs text-gray-500 uppercase tracking-wider">
            <div>Code</div><div>Name</div><div>Description</div>
          </div>
          {codes.filter(c => c.is_active).length === 0 && (
            <p className="text-gray-600 text-sm px-4 py-4">
              No active service codes for this carrier — activate them in the WMS carrier contracts panel above.
            </p>
          )}
          {codes.filter(c => c.is_active).map(c => (
            <div key={c.id} className="grid grid-cols-[100px_1fr_2fr] min-w-[360px] px-4 py-2.5 border-b border-gray-800/50 last:border-0 items-center">
              <div className="font-mono text-sm text-white">{c.code}</div>
              <div className="text-sm text-gray-300">{c.name}</div>
              <div className="text-sm text-gray-500 truncate pr-2">{c.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ShippingMethodsTab({ webshop_id }: { webshop_id: string }) {
  const [methods,  setMethods]  = useState<ShippingMethod[]>([]);
  const [carriers, setCarriers] = useState<CarrierAccount[]>([]);
  const [senders,  setSenders]  = useState<Sender[]>([]);
  const [syncing,  setSyncing]  = useState(false);
  const [syncMsg,  setSyncMsg]  = useState('');
  const [editing,  setEditing]  = useState<ShippingMethod|null>(null);
  const [cfg,      setCfg]      = useState<Partial<ShippingMethod>>({});
  const [serviceCodes, setServiceCodes] = useState<ServiceCode[]>([]);
  const [saving,   setSaving]   = useState(false);
  // Delivery promises of the method being edited (its own PUT, so a manager can fix a
  // promise without touching the carrier mapping, which is admin-only).
  const [promises, setPromises] = useState<Promise_[]>([]);
  const [promiseMsg, setPromiseMsg] = useState('');
  const [savingPromises, setSavingPromises] = useState(false);
  // Countries come from carrier_countries, written by the pickup-point syncs.
  // This used to be a hardcoded 9-entry array while the WMS actually held
  // points for 28, so the toggles could not express what we ship. Falls back to
  // the old list only if the request fails, so the row is never empty.
  const [countries, setCountries] = useState<string[]>(COUNTRIES);
  const load = useCallback(async () => {
    const [mr,cr,sr,kr] = await Promise.all([
      authFetch(`${API}/shipping/methods?webshop_id=${webshop_id}`),
      authFetch(`${API}/shipping/carriers?webshop_id=${webshop_id}`),
      authFetch(`${API}/shipping/senders?webshop_id=${webshop_id}`),
      authFetch(`${API}/shipping/carrier-countries`),
    ]);
    if (mr.ok) setMethods(await mr.json());
    if (cr.ok) setCarriers(await cr.json());
    if (sr.ok) setSenders(await sr.json());
    if (kr.ok) {
      const byCarrier: Record<string, { country: string }[]> = await kr.json();
      const all = Array.from(new Set(
        Object.values(byCarrier).flat().map(c => c.country))).sort();
      if (all.length) setCountries(all);
    }
  }, [webshop_id]);
  useEffect(() => { load(); }, [load]);
  // Reload active service codes when carrier changes in mapping form
  useEffect(() => {
    if (!cfg.carrier_account_id) { setServiceCodes([]); return; }
    authFetch(`${API}/shipping/service-codes?carrier_account_id=${cfg.carrier_account_id}`)
      .then(r => r.ok ? r.json() : [])
      .then((codes: ServiceCode[]) => setServiceCodes(codes.filter((c: ServiceCode) => c.is_active)));
  }, [cfg.carrier_account_id]);

  const sync = async () => {
    setSyncing(true); setSyncMsg('');
    const r = await authFetch(`${API}/shipping/methods/sync`, { method:'POST', body:JSON.stringify({ webshop_id }) });
    const d = await r.json().catch(()=>({}));
    setSyncMsg(r.ok ? `Synced ${(d as any).synced} methods` : (d as any).detail||'Sync failed');
    setSyncing(false); load();
  };
  const startEdit = (m: ShippingMethod) => {
    setEditing(m);
    setCfg({ carrier_account_id:m.carrier_account_id, service_code:m.service_code??'',
      fallback_carrier_id:m.fallback_carrier_id, sender_id:m.sender_id,
      customer_type:m.customer_type??'private', pickup_point_required:!!m.pickup_point_required,
      home_delivery:!!m.home_delivery, countries:m.countries??[],
      default_weight_kg:m.default_weight_kg??0.01,
      free_over_threshold:!!m.free_over_threshold, standard_rate:m.standard_rate??null });
    setPromises((m.promises ?? []).map(p => ({ ...p })));
    setPromiseMsg('');
  };
  const savePromises = async () => {
    if (!editing) return; setSavingPromises(true); setPromiseMsg('');
    const r = await authFetch(`${API}/shipping/methods/${editing.id}/promises`,
      { method:'PUT', body:JSON.stringify({ rows: promises }) });
    const d = await r.json().catch(() => ({}));
    setPromiseMsg(r.ok ? `Saved ${(d as any).rows} promise${(d as any).rows === 1 ? '' : 's'}` : ((d as any).detail || 'Save failed'));
    setSavingPromises(false); load();
  };
  const setPromise = (i: number, patch: Partial<Promise_>) =>
    setPromises(ps => ps.map((p, j) => j === i ? { ...p, ...patch } : p));
  const saveCfg = async () => {
    if (!editing) return; setSaving(true);
    await authFetch(`${API}/shipping/methods/${editing.id}/config`, { method:'PUT', body:JSON.stringify(cfg) });
    setSaving(false); setEditing(null); load();
  };
  const toggleCC = (cc: string) => {
    const cur = (cfg.countries as string[])??[];
    setCfg(p => ({ ...p, countries: cur.includes(cc) ? cur.filter(c=>c!==cc) : [...cur,cc] }));
  };
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <button onClick={sync} disabled={syncing} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white text-sm rounded-lg">
          {syncing ? 'Syncing…' : '↻ Sync from platform'}
        </button>
        {syncMsg && <span className="text-sm text-gray-400">{syncMsg}</span>}
      </div>
      {editing && (
        <div className="bg-[#1a1d27] border border-blue-800/50 rounded-xl p-4 mb-4">
          <div className="flex items-start justify-between mb-4">
            <div><p className="text-white font-medium">{editing.name}</p><p className="text-gray-500 text-xs">Map to carrier service</p></div>
            <button onClick={() => setEditing(null)} className="text-gray-500 hover:text-white text-lg">✕</button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Carrier *</label>
              <select value={cfg.carrier_account_id??''} onChange={e => setCfg(p=>({...p,carrier_account_id:Number(e.target.value)||undefined}))}
                className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none">
                <option value="">— none —</option>
                {carriers.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Service Code *</label>
              {serviceCodes.length > 0 ? (
                <select value={cfg.service_code??''}
                  onChange={e => setCfg(p=>({...p,service_code:e.target.value}))}
                  className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none">
                  <option value="">— select service code —</option>
                  {serviceCodes.map(sc => (
                    <option key={sc.code} value={sc.code}>{sc.code}{sc.name ? ` — ${sc.name}` : ''}</option>
                  ))}
                </select>
              ) : (
                <input value={cfg.service_code??''}
                  onChange={e => setCfg(p=>({...p,service_code:e.target.value}))}
                  placeholder={cfg.carrier_account_id ? 'No active codes — add in Service Codes tab' : 'Select a carrier first'}
                  className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none" />
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Sender</label>
              <select value={cfg.sender_id??''} onChange={e => setCfg(p=>({...p,sender_id:Number(e.target.value)||undefined}))}
                className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none">
                <option value="">— none —</option>
                {senders.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Fallback Carrier</label>
              <select value={cfg.fallback_carrier_id??''} onChange={e => setCfg(p=>({...p,fallback_carrier_id:Number(e.target.value)||undefined}))}
                className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none">
                <option value="">— none —</option>
                {carriers.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Customer Type</label>
              <select value={cfg.customer_type??'private'} onChange={e => setCfg(p=>({...p,customer_type:e.target.value}))}
                className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none">
                <option value="private">Private</option>
                <option value="business">Business</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Default Weight (kg)</label>
              <input type="number" step="0.01" value={cfg.default_weight_kg??0.01}
                onChange={e => setCfg(p=>({...p,default_weight_kg:parseFloat(e.target.value)||0.01}))}
                className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none" />
            </div>
          </div>
          <div className="flex gap-4 mb-3">
            <Chk label="Pickup point required" checked={!!cfg.pickup_point_required} onChange={v => setCfg(p=>({...p,pickup_point_required:v}))} />
            <Chk label="Home delivery"         checked={!!cfg.home_delivery}         onChange={v => setCfg(p=>({...p,home_delivery:v}))} />
          </div>
          {/* Returns: used to compute the original-shipping reclaim on the Manual refunds tab */}
          <div className="mb-3 p-3 rounded-lg border border-gray-700/60 bg-gray-800/30">
            <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Returns — original shipping reclaim</div>
            <div className="flex items-center gap-6 flex-wrap">
              <Chk label="Free over the free-shipping threshold" checked={!!cfg.free_over_threshold} onChange={v => setCfg(p=>({...p,free_over_threshold:v}))} />
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Standard rate</label>
                <span className="text-gray-500 text-sm">€</span>
                <input type="number" step="0.01" min="0" value={cfg.standard_rate??''} placeholder="—"
                  onChange={e => setCfg(p=>({...p,standard_rate:e.target.value===''?null:parseFloat(e.target.value)}))}
                  className="w-24 bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-2 py-1 focus:outline-none" />
              </div>
            </div>
            <p className="text-gray-600 text-xs mt-2">When this method was given free and the return drops the kept value below the threshold, the WMS reclaims the standard rate minus what the customer actually paid.</p>
          </div>
          <div className="mb-4">
            <label className="block text-xs text-gray-500 mb-2">Countries</label>
            <div className="flex flex-wrap gap-2">
              {countries.map(cc => {
                const on = ((cfg.countries??[]) as string[]).includes(cc);
                return (
                  <button key={cc} onClick={() => toggleCC(cc)}
                    className={`px-3 py-1 text-xs rounded-full font-mono transition-colors ${on ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                    {cc}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={saveCfg} disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
              {saving ? 'Saving…' : 'Save mapping'}
            </button>
            <button onClick={() => setEditing(null)} className="px-4 py-2 bg-gray-700 text-gray-300 text-sm rounded-lg">Cancel</button>
          </div>
          {/* Delivery promise — what the store's terms tell the customer, per destination country */}
          <div className="mt-5 pt-4 border-t border-gray-800">
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <p className="text-white text-sm font-medium">Delivery promise</p>
                <p className="text-gray-500 text-xs">Working days from dispatch, as the store's terms state them, per destination country. The parcel statistics compare every journey with this.</p>
              </div>
              <button onClick={() => setPromises(ps => [...ps, { country: '', days_min: 1, days_max: 3, note: '' }])}
                className="px-2 py-1 text-xs rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300">+ Country</button>
            </div>
            {promises.length === 0 && <p className="text-gray-600 text-xs mb-2">No promise recorded for this method.</p>}
            {promises.map((p, i) => (
              <div key={i} className="grid grid-cols-[70px_70px_70px_1fr_30px] gap-2 items-center mb-2">
                <input value={p.country} maxLength={2} placeholder="CC"
                  onChange={e => setPromise(i, { country: e.target.value.toUpperCase() })}
                  className="bg-[#0f1117] border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-white font-mono uppercase" />
                <input type="number" min={0} value={p.days_min}
                  onChange={e => setPromise(i, { days_min: Number(e.target.value) })}
                  className="bg-[#0f1117] border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-white" title="min working days" />
                <input type="number" min={0} value={p.days_max}
                  onChange={e => setPromise(i, { days_max: Number(e.target.value) })}
                  className="bg-[#0f1117] border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-white" title="max working days" />
                <input value={p.note ?? ''} placeholder={p.source_url ? `note · source: ${p.source_url}` : 'note'}
                  onChange={e => setPromise(i, { note: e.target.value })}
                  className="bg-[#0f1117] border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-gray-300" />
                <button onClick={() => setPromises(ps => ps.filter((_, j) => j !== i))}
                  className="text-gray-500 hover:text-red-400 text-sm" title="Remove">✕</button>
              </div>
            ))}
            <div className="flex items-center gap-3 mt-2">
              <button onClick={savePromises} disabled={savingPromises}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
                {savingPromises ? 'Saving…' : 'Save promise'}
              </button>
              {promiseMsg && <span className="text-xs text-gray-400">{promiseMsg}</span>}
              {(editing.promises ?? []).some(p => p.checked_at) && (
                <span className="text-xs text-gray-600">checked {(editing.promises ?? []).map(p => p.checked_at).filter(Boolean).sort().slice(-1)[0]}</span>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="bg-[#1a1d27] border border-gray-800 rounded-xl overflow-x-auto">
        <div className="grid grid-cols-[1fr_140px_100px_90px_1fr_70px] min-w-[700px] px-4 py-2 border-b border-gray-800 text-xs text-gray-500 uppercase tracking-wider">
          <div>Method name</div><div>Carrier</div><div>Code</div><div>Orders filter</div><div>Promise (working days)</div><div></div>
        </div>
        {methods.length===0 && <p className="text-gray-600 text-sm px-4 py-6 text-center">Click "Sync from platform" to import shipping methods.</p>}
        {methods.map(m => {
          const carrier = carriers.find(c => c.id===m.carrier_account_id);
          return (
            <div key={m.id}
              className={`grid grid-cols-[1fr_140px_100px_90px_1fr_70px] min-w-[700px] px-4 py-3 border-b border-gray-800/50 last:border-0 items-center ${!m.is_active?'opacity-40':''}`}>
              <div className="text-sm text-white truncate pr-2">{m.name}</div>
              <div className="text-xs text-gray-400 truncate">{carrier?.label??'—'}</div>
              <div className="font-mono text-xs text-gray-400">{m.service_code??'—'}</div>
              <div>{carrier && <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded">
                {CARRIER_CODES.find(x => x.value===carrier.carrier_code)?.pill ?? carrier.carrier_code}</span>}</div>
              <div className="text-xs text-gray-400 truncate" title={(m.promises ?? []).map(p => `${p.country} ${promiseText(p)}${p.note ? ' — ' + p.note : ''}`).join('\n')}>
                {(m.promises ?? []).length === 0
                  ? <span className="text-yellow-600/80">none</span>
                  : (m.promises ?? []).map(p => <span key={p.country} className="mr-2"><span className="font-mono text-gray-500">{p.country}</span> {promiseText(p)}{p.note ? '*' : ''}</span>)}
              </div>
              <div className="flex justify-end">
                <button onClick={() => startEdit(m)}
                  className={`px-2 py-1 text-xs rounded-lg ${m.config_id ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-yellow-900/40 hover:bg-yellow-800/60 text-yellow-400'}`}>
                  {m.config_id ? 'Edit' : 'Map'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function PickupPointsTab({ webshop_id }: { webshop_id: string }) {
  const [carriers, setCarriers]   = useState<CarrierAccount[]>([]);
  const [lists, setLists]         = useState<any[]>([]);
  const [syncing, setSyncing]     = useState<number | null>(null);
  const [syncMsg, setSyncMsg]     = useState('');

  const loadCarriers = () =>
    authFetch(`${API}/shipping/carriers?webshop_id=${webshop_id}`)
      .then(r => r.ok ? r.json() : []).then(setCarriers);

  const loadLists = () =>
    authFetch(`${API}/shipping/pickup-lists?webshop_id=${webshop_id}`)
      .then(r => r.ok ? r.json() : []).then(setLists);

  useEffect(() => { loadCarriers(); loadLists(); }, [webshop_id]);

  const syncPoints = async (carrierId: number, country: string) => {
    setSyncing(carrierId); setSyncMsg('');
    const r = await authFetch(
      `${API}/shipping/carriers/${carrierId}/sync-pickup-points?country=${country}`,
      { method: 'POST' }
    );
    const d = await r.json().catch(() => ({}));
    setSyncMsg(r.ok ? `Synced ${(d as any).synced} pickup points for ${country}` : (d as any).detail || 'Sync failed');
    setSyncing(null);
    loadLists();
  };

  const dpdCarriers = carriers.filter(c => c.carrier_code === 'dpd_baltics');

  // ── Browse points of one list ──
  const [openList, setOpenList] = useState<number | null>(null);
  const [points, setPoints] = useState<any[]>([]);
  const [pointsQ, setPointsQ] = useState('');
  const [loadingPoints, setLoadingPoints] = useState(false);

  const loadPoints = async (lid: number, q: string) => {
    setLoadingPoints(true);
    const r = await authFetch(`${API}/shipping/pickup-lists/${lid}/points?q=${encodeURIComponent(q)}`);
    setPoints(r.ok ? await r.json() : []);
    setLoadingPoints(false);
  };

  const toggleList = (lid: number) => {
    if (openList === lid) { setOpenList(null); return; }
    setOpenList(lid); setPointsQ(''); loadPoints(lid, '');
  };

  // ── Learned code mappings ──
  const [maps, setMaps] = useState<any[]>([]);
  const [mapQ, setMapQ] = useState('');
  const [editMap, setEditMap] = useState<number | null>(null);
  const [editPudo, setEditPudo] = useState('');
  const [mapMsg, setMapMsg] = useState('');

  const loadMaps = (q = '') =>
    authFetch(`${API}/shipping/pickup-map?webshop_id=${webshop_id}&q=${encodeURIComponent(q)}`)
      .then(r => r.ok ? r.json() : []).then(setMaps);

  useEffect(() => { loadMaps(); }, [webshop_id]);

  const saveMap = async (id: number) => {
    setMapMsg('');
    const r = await authFetch(`${API}/shipping/pickup-map/${id}`, {
      method: 'PUT', body: JSON.stringify({ pudo_id: editPudo }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setMapMsg((d as any).detail || 'Update failed');
      return;
    }
    setEditMap(null); setEditPudo('');
    loadMaps(mapQ);
  };

  const deleteMap = async (id: number) => {
    if (!window.confirm('Forget this mapping? The next label will re-match it by address.')) return;
    const r = await authFetch(`${API}/shipping/pickup-map/${id}`, { method: 'DELETE' });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setMapMsg((d as any).detail || 'Delete failed');
      return;
    }
    loadMaps(mapQ);
  };

  return (
    <div className="space-y-4">
      <p className="text-gray-500 text-sm">
        Import DPD pickup points so the WMS can automatically match customer pickup point
        selections to the right location. In some countries DPD delivers through a partner
        network (e.g. PostNord parcel shops in Finland) — the shop selected in the webshop
        and the point sent to DPD are the same place under two different codes.
      </p>

      {dpdCarriers.length === 0 && (
        <p className="text-gray-600 text-sm">No DPD carrier configured for this store.</p>
      )}

      {dpdCarriers.map(c => (
        <div key={c.id} className="bg-[#1a1d27] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-white font-medium text-sm">{c.label}</div>
              <div className="text-gray-500 text-xs">Sync pickup points by country</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {['FI','EE','LV','LT','SE','NO','PL','DE'].map(cc => (
              <button key={cc} onClick={() => syncPoints(c.id!, cc)}
                disabled={syncing === c.id}
                className="px-3 py-1.5 text-xs font-mono bg-gray-800 hover:bg-blue-600 hover:text-white text-gray-300 rounded-lg transition-colors disabled:opacity-40">
                {syncing === c.id ? '…' : cc}
              </button>
            ))}
          </div>
          {syncMsg && <p className="text-sm text-gray-400">{syncMsg}</p>}
        </div>
      ))}

      {/* Coverage first: a carrier with NO locker list is the thing worth seeing, and
          it used to be invisible — the page only rendered lists that existed, so a
          never-synced carrier (Bring) simply was not there. */}
      {carriers.length > 0 && (
        <div className="bg-[#1a1d27] border border-gray-800 rounded-xl overflow-hidden">
          <div className="grid grid-cols-[1fr_120px_150px_110px] px-4 py-2 border-b border-gray-800 text-xs text-gray-500 uppercase tracking-wider">
            <div>Carrier</div><div>Code</div><div>Locker lists</div><div className="text-right">Points</div>
          </div>
          {carriers.map((c: any) => {
            const mine = lists.filter((l: any) => l.carrier_code === c.carrier_code);
            const points = mine.reduce((n: number, l: any) => n + Number(l.point_count || 0), 0);
            return (
              <div key={c.id}
                   className="grid grid-cols-[1fr_120px_150px_110px] px-4 py-2.5 border-b border-gray-800/50 last:border-0 items-center text-sm">
                <div className="text-white">{c.label || c.carrier_code}</div>
                <div className="font-mono text-gray-500 text-xs">{c.carrier_code}</div>
                <div>
                  {mine.length === 0
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-900/60 text-amber-300">never synced</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">
                        {mine.length} {mine.length === 1 ? 'country' : 'countries'}
                      </span>}
                </div>
                <div className="text-right text-gray-400">{points ? points.toLocaleString() : '—'}</div>
              </div>
            );
          })}
        </div>
      )}

      {lists.length > 0 && (
        <div className="bg-[#1a1d27] border border-gray-800 rounded-xl overflow-x-auto">
          <div className="grid grid-cols-[1fr_70px_170px_90px] min-w-[520px] px-4 py-2 border-b border-gray-800 text-xs text-gray-500 uppercase tracking-wider">
            <div>List</div><div>Country</div><div>Network</div><div className="text-right">Points</div>
          </div>
          {lists.map((l: any) => (
            <React.Fragment key={l.id}>
              <div
                onClick={() => toggleList(l.id)}
                className="grid grid-cols-[1fr_70px_170px_90px] min-w-[520px] px-4 py-3 border-b border-gray-800/50 last:border-0 items-center cursor-pointer hover:bg-gray-800/30">
                <div className="text-white text-sm">
                  <span className="text-gray-600 mr-2">{openList === l.id ? '▾' : '▸'}</span>
                  {l.name}
                </div>
                <div className="font-mono text-gray-400 text-sm">{l.country}</div>
                <div>
                  {Number(l.postnord_pct) >= 60
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-900/60 text-cyan-300">PostNord partner shops</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">DPD / partner shops</span>}
                </div>
                <div className="text-right text-gray-400 text-sm">{l.point_count}</div>
              </div>
              {openList === l.id && (
                <div className="px-4 py-3 border-b border-gray-800/50 bg-gray-900/40">
                  <input
                    value={pointsQ}
                    onChange={e => setPointsQ(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') loadPoints(l.id, pointsQ); }}
                    placeholder="Search name, city or postal code — Enter to search"
                    className="w-full mb-2 bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-600"
                  />
                  {loadingPoints ? (
                    <p className="text-gray-500 text-sm">Loading…</p>
                  ) : (
                    <div className="max-h-72 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-500 uppercase tracking-wider text-left">
                            <th className="py-1 pr-3">Code</th>
                            <th className="py-1 pr-3">Name</th>
                            <th className="py-1 pr-3">Address</th>
                            <th className="py-1 pr-3">City</th>
                            <th className="py-1">Postal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {points.map((p: any) => (
                            <tr key={p.id} className="border-t border-gray-800/50 text-gray-300">
                              <td className="py-1 pr-3 font-mono text-gray-400">{p.external_id}</td>
                              <td className="py-1 pr-3">{p.name}</td>
                              <td className="py-1 pr-3 text-gray-400">{p.address}</td>
                              <td className="py-1 pr-3 text-gray-400">{p.city}</td>
                              <td className="py-1 font-mono text-gray-400">{p.postal_code}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-gray-600 text-xs mt-2">
                        Showing first {points.length} points{points.length >= 100 ? ' — refine the search to see more' : ''}.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Learned code mappings: platform pickup code → carrier pudo id */}
      <div className="bg-[#1a1d27] border border-gray-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-800">
          <div className="text-white font-medium text-sm">Learned pickup point mappings</div>
          <p className="text-gray-500 text-xs mt-1">
            Webshop pickup code (e.g. PostNord) → DPD point, learned automatically from address
            matches when labels are printed. If a mapping is wrong, correct the DPD code here —
            all future labels for that pickup point will use the corrected value.
          </p>
        </div>
        <div className="px-4 py-2 border-b border-gray-800">
          <input
            value={mapQ}
            onChange={e => setMapQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') loadMaps(mapQ); }}
            placeholder="Search code, DPD id, name or city — Enter to search"
            className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-600"
          />
        </div>
        {mapMsg && <p className="px-4 py-2 text-sm text-red-400">{mapMsg}</p>}
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[720px]">
            <thead>
              <tr className="text-gray-500 uppercase tracking-wider text-left border-b border-gray-800">
                <th className="px-4 py-2">Webshop code</th>
                <th className="px-4 py-2">DPD point</th>
                <th className="px-4 py-2">Name / address</th>
                <th className="px-4 py-2">Matched by</th>
                <th className="px-4 py-2">Updated</th>
                <th className="px-4 py-2 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {maps.map((m: any) => (
                <tr key={m.id} className="border-b border-gray-800/50 last:border-0 text-gray-300">
                  <td className="px-4 py-2 font-mono text-white">{m.source_code}</td>
                  <td className="px-4 py-2 font-mono">
                    {editMap === m.id ? (
                      <input autoFocus value={editPudo}
                        onChange={e => setEditPudo(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveMap(m.id);
                          if (e.key === 'Escape') { setEditMap(null); setEditPudo(''); }
                        }}
                        className="w-24 bg-gray-900 border border-blue-600 rounded px-1.5 py-0.5 text-xs text-white" />
                    ) : (
                      <span className={m.pudo_exists ? 'text-cyan-300' : 'text-red-400'} title={m.pudo_exists ? '' : 'Not found in the synced list!'}>
                        {m.pudo_id}{!m.pudo_exists && ' ⚠'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {m.pickup_name}
                    {(m.pudo_address || m.pudo_city) && (
                      <span className="text-gray-500"> — {[m.pudo_address, m.pudo_city, m.pudo_postal].filter(Boolean).join(', ')}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-500">{m.matched_by}</td>
                  <td className="px-4 py-2 text-gray-500">{m.updated_at ? new Date(m.updated_at).toLocaleDateString('fi-FI') : ''}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {editMap === m.id ? (
                      <>
                        <button onClick={() => saveMap(m.id)} className="text-blue-400 hover:text-blue-300 mr-2">Save</button>
                        <button onClick={() => { setEditMap(null); setEditPudo(''); }} className="text-gray-500 hover:text-gray-300">✕</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { setEditMap(m.id); setEditPudo(m.pudo_id); setMapMsg(''); }}
                          className="text-gray-500 hover:text-blue-400 mr-2">Edit</button>
                        <button onClick={() => deleteMap(m.id)} className="text-gray-600 hover:text-red-400">Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {maps.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-4 text-gray-600 text-center">No learned mappings yet — they appear as pickup labels are printed.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Storefront offering (Shopify cart widget) ────────────────────────────────

interface Readiness { key: 'shopify' | 'carrier' | 'contract' | 'label' | 'points' | 'sender'; ok: boolean; note: string }
interface StorefrontMethod {
  id: number; country: string; carrier_code: string; delivery_type: string;
  brand_label: string; wms_method_name: string; shopify_rate_name?: string | null;
  price: number; currency: string; free_threshold: number | null; free_threshold_currency?: string | null;
  enabled: boolean; sort_order: number;
  carrier_confirmed?: boolean; ready?: boolean; readiness?: Readiness[];
  gone_from_shopify_at?: string | null; auto_disabled_at?: string | null;
}
const READY_LABEL: Record<Readiness['key'], string> = {
  shopify: 'Shopify', carrier: 'carrier', contract: 'contract', label: 'label', points: 'pickup points', sender: 'sender',
};
const money = (amount: number | null | undefined, ccy?: string | null) =>
  amount == null ? '—' : `${Number(amount).toFixed(ccy === 'SEK' || ccy === 'NOK' || ccy === 'DKK' ? 0 : 2)} ${ccy === 'EUR' || !ccy ? '€' : ccy}`;

interface RowHealth {
  status: 'ok' | 'price_mismatch' | 'rate_missing' | 'unmapped' | 'no_zone' | 'off';
  note: string;
  rate_name: string | null;
  rate_price: number | null;
}
interface Health {
  connected: boolean;
  reason?: string;
  rows: Record<number, RowHealth>;
  country_rates: Record<string, { name: string; price: number | null }[]>;
}

function StorefrontTab({ webshop_id }: { webshop_id: string }) {
  const [rows, setRows] = useState<StorefrontMethod[]>([]);
  const [health, setHealth] = useState<Health | null>(null);
  const [edits, setEdits] = useState<Record<number, Partial<StorefrontMethod>>>({});
  const [saving, setSaving] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/shipping/storefront-methods?webshop_id=${webshop_id}`);
    if (r.ok) setRows(await r.json());
    // Health check reads the store's real Shopify rates — slower, so separate
    authFetch(`${API}/shipping/storefront-methods/health?webshop_id=${webshop_id}`)
      .then(hr => hr.ok ? hr.json() : null).then(setHealth).catch(() => setHealth(null));
  }, [webshop_id]);
  useEffect(() => { load(); }, [load]);

  const val = (r: StorefrontMethod, k: keyof StorefrontMethod) =>
    edits[r.id]?.[k] !== undefined ? (edits[r.id] as any)[k] : (r as any)[k];
  const setVal = (id: number, k: string, v: any) =>
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [k]: v } }));

  const save = async (r: StorefrontMethod, extra: Record<string, any> = {}) => {
    setSaving(r.id);
    const body: any = { ...edits[r.id], ...extra };
    const res = await authFetch(`${API}/shipping/storefront-methods/${r.id}`, { method: 'PUT', body: JSON.stringify(body) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) setMsg(typeof d.detail === 'string' ? d.detail : (d.detail?.message || 'Could not save'));
    setEdits(prev => { const n = { ...prev }; delete n[r.id]; return n; });
    setSaving(null);
    load();
  };

  // Switching on is refused by the server unless every readiness item is ✓ (409 lists what is missing)
  const toggle = async (r: StorefrontMethod) => {
    const res = await authFetch(`${API}/shipping/storefront-methods/${r.id}`, { method: 'PUT', body: JSON.stringify({ enabled: !r.enabled }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) setMsg(`${r.shopify_rate_name || r.wms_method_name}: ${typeof d.detail === 'string' ? d.detail : (d.detail?.message || 'not switched')}`);
    else if (d.label) setMsg(`${r.shopify_rate_name || r.wms_method_name}: label recipe — ${d.label}`);
    else setMsg('');
    load();
  };

  const syncFromShopify = async () => {
    setSyncing(true);
    const r = await authFetch(`${API}/shipping/methods/sync`, { method: 'POST', body: JSON.stringify({ webshop_id }) });
    const d = await r.json().catch(() => ({}));
    const off: string[] = (d as any).switched_off || [];
    setMsg(r.ok ? `Synced ${(d as any).synced} rates from Shopify${off.length ? ` — switched off (gone from Shopify): ${off.join(', ')}` : ''}`
               : ((d as any).detail || 'Sync failed'));
    setSyncing(false);
    load();
  };

  const removeRow = async (r: StorefrontMethod) => {
    if (!window.confirm(`Delete ${r.wms_method_name}?`)) return;
    await authFetch(`${API}/shipping/storefront-methods/${r.id}`, { method: 'DELETE' });
    load();
  };

  const countries = Array.from(new Set(rows.map(r => r.country)));

  const readinessLine = (r: StorefrontMethod) => {
    const items = r.readiness || [];
    if (!items.length) return null;
    const bad = items.filter(x => !x.ok);
    if (!bad.length) {
      const label = items.find(x => x.key === 'label');
      return <div className="text-[11px] mt-0.5 text-green-500">✓ ready{label?.note ? ` · label ${label.note}` : ''}</div>;
    }
    return (
      <div className={`text-[11px] mt-0.5 ${r.enabled ? 'text-red-400' : 'text-amber-400'}`}>
        {bad.map(x => <div key={x.key}>✗ {READY_LABEL[x.key]}: {x.note}</div>)}
      </div>
    );
  };

  const statusLine = (r: StorefrontMethod) => {
    const h = health?.rows?.[r.id];
    if (!h || h.status === 'off') return null;
    return (
      <div className={`text-[11px] mt-0.5 ${h.status === 'ok' ? 'text-green-400' : 'text-red-400'}`}>
        {h.status === 'ok' ? `✓ matches checkout${h.note ? ` (${h.note})` : ''}` : `✗ ${h.note}`}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={syncFromShopify} disabled={syncing}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-300 text-sm rounded-lg">
          {syncing ? 'Syncing…' : '↻ Sync from Shopify'}
        </button>
        {msg && <span className="text-sm text-gray-400">{msg}</span>}
      </div>
      <p className="text-gray-500 text-sm">
        Shipping methods are the store's own Shopify rates, synced here. Name, price, currency and
        the free-from amount are managed in Shopify — change them there, then Sync. The WMS adds:
        whether the method is offered (On), the carrier that prints its labels, locker/home, and the
        brand customers see in the cart. To add a method: create the rate in Shopify → Sync → choose
        the carrier → switch it on. A rate that disappears from Shopify is switched off automatically.
      </p>
      <div className="bg-[#141720] border border-gray-800 rounded-lg px-4 py-3 text-xs text-gray-400 space-y-1.5">
        <div className="text-gray-300 font-medium">Two checks are shown under each rate:</div>
        <div>
          <span className="text-green-500 font-medium">✓ ready</span> / <span className="text-amber-400 font-medium">✗ …</span>
          {' '}— can the WMS print labels for it? The carrier must be chosen (a sync only suggests one), the
          store's account must be on a WMS carrier contract, the carrier service must be active (Packeta:
          mapped under Packeta services), a pickup method needs that carrier's pickup points for the country,
          and the store needs a sender. <span className="text-gray-300">A method can only be switched on when
          every item is ✓</span> — so nothing customers can choose is left without a label.
        </div>
        <div>
          <span className="text-green-400 font-medium">✓ matches checkout</span> — a separate check: the name and
          price here agree with the live Shopify rate the customer sees.
        </div>
      </div>
      {health && !health.connected && (
        <div className="bg-amber-900/30 border border-amber-800 rounded-lg px-4 py-2.5 text-amber-300 text-sm">
          ⚠ Live rate check unavailable: {health.reason}
        </div>
      )}
      {countries.map(c => (
        <div key={c} className="bg-[#1a1d27] border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-800 flex items-center gap-2">
            <span className="text-white font-semibold text-sm">{c}</span>
            <span className="text-gray-600 text-xs">{rows.filter(r => r.country===c && r.enabled).length} of {rows.filter(r => r.country===c).length} active</span>
          </div>
          <div className="grid grid-cols-[46px_1.7fr_120px_1fr_92px_84px_90px_60px] px-4 py-2 border-b border-gray-800 text-xs text-gray-500 uppercase tracking-wider">
            <div>On</div><div>Shopify rate</div><div>Carrier</div><div>Brand (customer sees)</div><div>Type</div><div>Price</div><div>Free from</div><div></div>
          </div>
          {rows.filter(r => r.country === c).map(r => (
            <div key={r.id} className={`grid grid-cols-[46px_1.7fr_120px_1fr_92px_84px_90px_60px] px-4 py-2 border-b border-gray-800/50 last:border-0 items-center gap-2 ${!r.enabled ? 'opacity-60' : ''}`}>
              <input type="checkbox" checked={r.enabled} onChange={() => toggle(r)}
                disabled={!r.enabled && r.ready === false}
                title={!r.enabled && r.ready === false ? 'Not ready — see the ✗ lines' : ''}
                className="rounded border-gray-600 bg-gray-700 accent-blue-500 cursor-pointer disabled:cursor-not-allowed" />
              <div>
                <div className="text-white text-sm">{r.shopify_rate_name || r.wms_method_name}</div>
                {r.gone_from_shopify_at && (
                  <div className="text-[11px] mt-0.5 text-red-400">
                    ✗ no longer in Shopify{r.auto_disabled_at ? ' — switched off automatically' : ''}
                  </div>
                )}
                {!r.gone_from_shopify_at && readinessLine(r)}
                {statusLine(r)}
              </div>
              <select value={val(r,'carrier_code')} onChange={e => setVal(r.id,'carrier_code',e.target.value)}
                className={`bg-gray-900 border rounded px-1.5 py-1 text-xs text-white w-full ${r.carrier_confirmed || edits[r.id]?.carrier_code ? 'border-gray-800' : 'border-amber-600'}`}
                title={r.carrier_confirmed ? '' : 'Suggested from the rate name — choose and Save to confirm'}>
                {CARRIER_CODES.map(c => <option key={c.value} value={c.value}>{c.pill}{!r.carrier_confirmed && !edits[r.id]?.carrier_code && c.value === r.carrier_code ? ' (suggested)' : ''}</option>)}
              </select>
              <input value={val(r,'brand_label')} onChange={e => setVal(r.id,'brand_label',e.target.value)}
                className="bg-gray-900 border border-gray-800 rounded px-2 py-1 text-sm text-white w-full" />
              <select value={val(r,'delivery_type')} onChange={e => setVal(r.id,'delivery_type',e.target.value)}
                className="bg-gray-900 border border-gray-800 rounded px-1.5 py-1 text-xs text-white w-full">
                <option value="pp">📦 Locker</option>
                <option value="hd">🚚 Home</option>
              </select>
              <div className="text-sm text-gray-300 text-right pr-2" title="Price comes from the Shopify rate">
                {money(r.price, r.currency)}
              </div>
              <div className="text-sm text-gray-400 text-right pr-2"
                   title="Free shipping from this order total — set in Shopify (the same rate twice: priced, and free with a minimum order)">
                {money(r.free_threshold, r.free_threshold_currency || r.currency)}
              </div>
              <div className="flex gap-1 justify-end">
                {(edits[r.id] || !r.carrier_confirmed) && (
                  <button onClick={() => save(r, r.carrier_confirmed || edits[r.id]?.carrier_code ? {} : { carrier_code: r.carrier_code })}
                    disabled={saving===r.id}
                    title={edits[r.id] ? '' : 'Confirm the suggested carrier'}
                    className="px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded">
                    {saving===r.id ? '…' : (edits[r.id] ? 'Save' : 'Confirm')}
                  </button>
                )}
                <button onClick={() => removeRow(r)} className="px-2 py-1 text-gray-600 hover:text-red-400 text-xs" title="Remove (re-appears on Sync if the rate still exists in Shopify)">✕</button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// Which cart a packed parcel goes into (Fernando, 2026-09-28): the label's carrier + the
// destination country → a cart name the packing screen shows after the label scan. One list
// for the whole warehouse (the carts are physical), first matching rule wins.
function DispatchCartsCard() {
  const [rules, setRules] = useState<{ carrier: string; countries: string[]; cart: string }[]>([]);
  const [carriers, setCarriers] = useState<string[]>([]);
  const [msg, setMsg] = useState('');
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    authFetch(`${API}/settings/dispatch-carts`).then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return; setRules(d.rules || []); setCarriers(d.carriers || []);
    }).catch(() => {});
  }, []);
  const upd = (i: number, patch: Partial<{ carrier: string; countries: string[]; cart: string }>) => {
    setRules(rs => rs.map((r, k) => (k === i ? { ...r, ...patch } : r))); setDirty(true);
  };
  const save = async () => {
    const res = await authFetch(`${API}/settings/dispatch-carts`, { method: 'PUT', body: JSON.stringify({ rules }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(d.detail || 'Could not save'); return; }
    setRules(d.rules || rules); setDirty(false); setMsg('✓ Saved'); setTimeout(() => setMsg(''), 2000);
  };
  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4">
      <div className="text-white text-sm font-medium">Dispatch carts</div>
      <div className="text-gray-500 text-xs mt-0.5 mb-3">
        After the label scan the packing screen says which cart the parcel goes into: the label&apos;s carrier plus the
        destination country. Countries empty = any country. The first matching rule wins. One list for the whole warehouse.
      </div>
      <div className="space-y-1.5">
        {rules.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
            <select value={r.carrier} onChange={e => upd(i, { carrier: e.target.value })}
              className="bg-[#0f1117] border border-gray-700 text-white rounded px-2 py-1.5">
              {carriers.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input value={r.countries.join(', ')} placeholder="countries, e.g. SE, NO (empty = any)"
              onChange={e => upd(i, { countries: e.target.value.split(/[,\s]+/).filter(Boolean).map(s => s.toUpperCase()) })}
              className="w-44 bg-[#0f1117] border border-gray-700 text-white rounded px-2 py-1.5 font-mono text-xs" />
            <span className="text-gray-500">→</span>
            <input value={r.cart} placeholder="cart name" onChange={e => upd(i, { cart: e.target.value })}
              className="w-36 bg-[#0f1117] border border-gray-700 text-white rounded px-2 py-1.5" />
            <button onClick={() => { setRules(rs => rs.filter((_, k) => k !== i)); setDirty(true); }} className="text-xs text-gray-500 hover:text-red-400">remove</button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button onClick={() => { setRules(rs => [...rs, { carrier: carriers[0] || 'dpd_baltics', countries: [], cart: '' }]); setDirty(true); }}
          className="px-3 py-1.5 text-xs rounded-lg bg-gray-800 text-gray-200">+ Rule</button>
        <button onClick={save} disabled={!dirty} className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white disabled:opacity-40">Save</button>
        <span className="text-xs text-gray-500">{msg}</span>
      </div>
    </div>
  );
}

function ShippingGeneralTab({ webshop }: { webshop: Webshop }) {
  const [useRealWeight, setUseRealWeight] = useState(false);
  const [prebuyLabels, setPrebuyLabels] = useState(false);
  const [prebuyStatus, setPrebuyStatus] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/settings/webshops`);
    if (!r.ok) return;
    const all = await r.json();
    const ws = all.find((w: any) => w.id === webshop.id);
    setUseRealWeight(!!ws?.config?.use_real_weight);
    setPrebuyLabels(!!ws?.config?.prebuy_labels);
    // Review shows only failures — this line is how a silently idle sweep
    // stays visible. Best-effort: the card renders fine without it.
    authFetch(`${API}/settings/prebuy-status`)
      .then(pr => (pr.ok ? pr.json() : null))
      .then(d => setPrebuyStatus(d))
      .catch(() => {});
  }, [webshop.id]);
  useEffect(() => { load(); }, [load]);

  // Read-merge-patch: the config object carries other keys (weights, note
  // logo, platform blocks) — always PATCH the whole merged object.
  const patchConfig = async (key: string, value: boolean) => {
    setSaving(true);
    const r = await authFetch(`${API}/settings/webshops`);
    const all = await r.json();
    const ws = all.find((w: any) => w.id === webshop.id);
    await authFetch(`${API}/settings/webshops/${webshop.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ config: { ...ws?.config, [key]: value } }),
    });
    setSaving(false);
  };

  const st = prebuyStatus?.stores?.[webshop.id];
  const nt = prebuyStatus?.notes?.[webshop.id];

  // Paytrail merchant credentials for automatic refunds (docs/PAYMENTS.md). The secret comes
  // back masked from the API and a masked value on save keeps the stored one (_merge_secrets).
  const [ptMerchant, setPtMerchant] = useState('');
  const [ptSecret, setPtSecret] = useState('');
  const [ptSaved, setPtSaved] = useState('');
  useEffect(() => {
    authFetch(`${API}/settings/webshops`).then(r => r.ok ? r.json() : []).then((all: any[]) => {
      const ws = all.find((w: any) => w.id === webshop.id);
      setPtMerchant(ws?.config?.paytrail?.merchant_id || '');
      setPtSecret(ws?.config?.paytrail?.secret || '');
    }).catch(() => {});
  }, [webshop.id]);
  const savePaytrail = async () => {
    setSaving(true);
    const r = await authFetch(`${API}/settings/webshops`);
    const all = await r.json();
    const ws = all.find((w: any) => w.id === webshop.id);
    const res = await authFetch(`${API}/settings/webshops/${webshop.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ config: { ...ws?.config, paytrail: { merchant_id: ptMerchant.trim(), secret: ptSecret } } }),
    });
    setSaving(false);
    setPtSaved(res.ok ? '✓ Saved' : 'Could not save');
    setTimeout(() => setPtSaved(''), 2000);
  };
  const fmtT = (s: string | null | undefined) =>
    s ? (fmtWh(s, 'short') || '—') : '—';

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4 flex items-center justify-between gap-4">
        <div>
          <div className="text-white text-sm font-medium">Use real order weight on labels</div>
          <div className="text-gray-500 text-xs mt-0.5">
            On: labels use the parcel weight reported by the platform (WGR &amp; Shopify orders).
            Off: the standard weight from each shipping method&apos;s settings is used.
            {webshop.platform === 'mcf' && ' MyCashflow orders carry no weight, so the standard weight always applies.'}
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none flex-none">
          <input
            type="checkbox"
            checked={useRealWeight}
            disabled={saving}
            onChange={e => { setUseRealWeight(e.target.checked); patchConfig('use_real_weight', e.target.checked); }}
          />
          <span className="text-gray-400 text-sm">{useRealWeight ? 'Real weight' : 'Standard weight'}</span>
        </label>
      </div>

      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4 flex items-center justify-between gap-4">
        <div>
          <div className="text-white text-sm font-medium">Pre-buy shipping labels</div>
          <div className="text-gray-500 text-xs mt-0.5">
            On: the WMS buys each new order&apos;s label within ~5 minutes of the order arriving and
            stores it; Print shipping labels then prints the stored labels. A failed buy shows up in
            Review (Label issue) the same day instead of on despatch morning.
            Off: labels are bought when Print shipping labels is pressed.
          </div>
          {prebuyLabels && (
            <div className="text-gray-600 text-xs mt-1">
              {st
                ? <>Bought today: {st.bought_today ?? 0} · last: {fmtT(st.last_bought_at)}
                    {prebuyStatus?.failing_now ? <span className="text-amber-500"> · failures waiting in Review: {prebuyStatus.failing_now}</span> : null}</>
                : 'No labels pre-bought yet — the sweep runs every 5 minutes.'}
            </div>
          )}
        </div>
        {/* A constant ACTION label, not a state readout: "Buying at print" next
            to an unchecked box read as "tick this to buy at print" — the exact
            opposite of what ticking does. Fernando misread it within the hour
            of it shipping, which settles the question. (Claude, 2026-08-31) */}
        <label className="flex items-center gap-2 cursor-pointer select-none flex-none">
          <input
            type="checkbox"
            checked={prebuyLabels}
            disabled={saving}
            onChange={e => { setPrebuyLabels(e.target.checked); patchConfig('prebuy_labels', e.target.checked); }}
          />
          <span className="text-gray-400 text-sm">Buy labels when the order arrives</span>
        </label>
      </div>

      <DispatchCartsCard />
      <PickupScheduleCard />
      <PackOrderCard />

      {webshop.platform === 'mcf' && (
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4">
          <div className="text-white text-sm font-medium">Paytrail — automatic refunds</div>
          <div className="text-gray-500 text-xs mt-0.5 mb-3">
            The merchant id and secret of this store&apos;s Paytrail account (Paytrail Merchant Panel → Settings → API credentials).
            Used only to refund accepted returns from Returns → Queued refunds, one call per refund. Switch the store on in
            Returns → Settings → Automatic refunds.
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <input value={ptMerchant} onChange={e => setPtMerchant(e.target.value)} placeholder="Merchant id (e.g. 375917)"
              className="w-48 px-3 py-2 rounded bg-[#0f1117] border border-gray-700 text-white text-sm font-mono focus:outline-none focus:border-blue-500" />
            <input type="password" value={ptSecret} onChange={e => setPtSecret(e.target.value)} placeholder="Secret key"
              className="flex-1 min-w-[240px] px-3 py-2 rounded bg-[#0f1117] border border-gray-700 text-white text-sm font-mono focus:outline-none focus:border-blue-500" />
            <button onClick={savePaytrail} disabled={saving || !ptMerchant.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg">Save</button>
            {ptSaved && <span className={`text-xs ${ptSaved.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{ptSaved}</span>}
          </div>
        </div>
      )}

      {/* No toggle: pre-generating a note costs nothing and calls no carrier, so
          there is nothing to opt into. The readout is here for the same reason
          as the pre-buy one — Review shows only failures, and a silently idle
          sweep would otherwise be invisible. (Claude, 2026-09-06) */}
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4">
        <div className="text-white text-sm font-medium">Pre-generated delivery notes</div>
        <div className="text-gray-500 text-xs mt-0.5">
          Always on: the WMS renders each new order&apos;s delivery note right after the order sync
          {webshop.platform === 'mcf' && " (for a MyCashflow store it fetches and stores the shop's own document)"}
          , and Print delivery notes loads the stored notes. A note is re-rendered automatically when the
          order, the template or the store logo changes. A failed render shows up in Review (Delivery note issue).
        </div>
        <div className="text-gray-600 text-xs mt-1">
          {nt
            ? <>Ready: {nt.ready} of {nt.total} orders in New
                {nt.failing ? <span className="text-amber-500"> · failures waiting in Review: {nt.failing}</span> : null}</>
            : 'No orders in New for this store.'}
        </div>
      </div>
    </div>
  );
}

const NOTE_LANG_NAMES: Record<string, string> = {
  en:'English', sv:'Svenska', fi:'Suomi', de:'Deutsch', da:'Dansk', no:'Norsk',
  es:'Español', et:'Eesti', lt:'Lietuvių', lv:'Latviešu', cs:'Čeština', hu:'Magyar',
  pl:'Polski', sk:'Slovenčina', sl:'Slovenščina', hr:'Hrvatski', nl:'Nederlands',
  fr:'Français', pt:'Português', bg:'Български', el:'Ελληνικά', it:'Italiano',
  ro:'Română', fx:'Français (BE)', nx:'Nederlands (BE)',
};

const NOTE_FIELD_SECTIONS: { title: string; fields: { key: string; label: string; multiline?: boolean }[] }[] = [
  { title: 'Header', fields: [
    { key: 'title', label: 'Note title (e.g. Receipt / return form)' },
    { key: 'for_order', label: '"for order" text (before the order number)' },
  ]},
  { title: 'Customer / order info labels', fields: [
    { key: 'customer_label', label: 'Customer' }, { key: 'date_label', label: 'Date' },
    { key: 'order_no_label', label: 'Order number' }, { key: 'payment_label', label: 'Payment' },
    { key: 'shipping_label', label: 'Delivery method' }, { key: 'pickup_label', label: 'Pickup point' },
  ]},
  { title: 'Return instructions', fields: [
    { key: 'instructions_title', label: 'Instructions title' },
    { key: 'instructions', label: 'Instructions (one step per line)', multiline: true },
    { key: 'terms_line', label: 'Terms line (small print under instructions)' },
  ]},
  { title: 'Items table', fields: [
    { key: 'col_product', label: 'Product column' }, { key: 'col_qty', label: 'Quantity column' },
    { key: 'col_return', label: 'Return qty column' }, { key: 'col_price', label: 'Price column' },
    { key: 'col_total', label: 'Total column' }, { key: 'product_no_label', label: 'Product number label' },
    { key: 'vat_label', label: 'VAT label' },
  ]},
  { title: 'Totals', fields: [
    { key: 'products_total_label', label: 'Products total' }, { key: 'shipping_cost_label', label: 'Shipping' },
    { key: 'payment_fee_label', label: 'Payment fee' }, { key: 'grand_total_label', label: 'Grand total' },
    { key: 'vat_share_label', label: '"of which VAT"' },
  ]},
  { title: 'Footer (bottom of every page)', fields: [
    { key: 'footer_company', label: 'Company block (name, address…)', multiline: true },
    { key: 'footer_ids', label: 'Registry / VAT ids block', multiline: true },
    { key: 'footer_contact', label: 'Contact block', multiline: true },
  ]},
];

function noteFileToDataUrl(file: File, maxBytes: number): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.size > maxBytes) { reject(new Error(`Image too large (max ${Math.round(maxBytes / 1024)} KB)`)); return; }
    if (!file.type.startsWith('image/')) { reject(new Error('Please choose an image file')); return; }
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}

function NoteTemplateTab({ webshop }: { webshop: Webshop }) {
  const [langs, setLangs] = useState<string[]>([]);
  const [saved, setSaved] = useState<Record<string, any>>({});
  const [defaults, setDefaults] = useState<any>({});
  const [lang, setLang] = useState('en');
  const [tpl, setTpl] = useState<any>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [storeLogo, setStoreLogo] = useState<string | null>(null);
  const [logoMsg, setLogoMsg] = useState('');

  const effective = useCallback((templates: Record<string, any>, dflt: any, lg: string) => (
    { ...dflt, ...(templates['en'] ?? {}), ...(templates[lg] ?? {}) }
  ), []);

  const loadStoreLogo = useCallback(async () => {
    const r = await authFetch(`${API}/settings/webshops`);
    if (!r.ok) return;
    const all = await r.json();
    const ws = all.find((x: any) => x.id === webshop.id);
    setStoreLogo(ws?.config?.note_logo ?? null);
  }, [webshop.id]);
  useEffect(() => { loadStoreLogo(); }, [loadStoreLogo]);

  const saveStoreLogo = async (dataUrl: string | null) => {
    const r = await authFetch(`${API}/settings/webshops`);
    const all = await r.json();
    const ws = all.find((x: any) => x.id === webshop.id);
    const cfg = { ...(ws?.config ?? {}) };
    if (dataUrl) cfg.note_logo = dataUrl; else delete cfg.note_logo;
    const res = await authFetch(`${API}/settings/webshops/${webshop.id}`, {
      method: 'PATCH', body: JSON.stringify({ config: cfg }),
    });
    if (res.ok) { setStoreLogo(dataUrl); setLogoMsg(dataUrl ? 'Logo saved — use Preview to see it on the note' : 'Logo removed'); }
    else setLogoMsg('Save failed');
  };

  const uploadStoreLogo = async (file: File | null) => {
    if (!file) return;
    setLogoMsg('');
    try {
      saveStoreLogo(await noteFileToDataUrl(file, 300 * 1024));
    } catch (e: any) {
      setLogoMsg(e.message || 'Upload failed');
    }
  };

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/note-templates?webshop_id=${webshop.id}`);
    if (!r.ok) return;
    const d = await r.json();
    setLangs(d.languages || []);
    setSaved(d.templates || {});
    setDefaults(d.default || {});
    setTpl({ ...(d.default || {}), ...((d.templates || {})['en'] ?? {}) });
  }, [webshop.id]);
  useEffect(() => { load(); }, [load]);

  const pickLang = (lg: string) => {
    setLang(lg);
    setTpl(effective(saved, defaults, lg));
    setMsg('');
  };

  const save = async () => {
    setBusy('save');
    const r = await authFetch(`${API}/note-templates/${webshop.id}/${lang}`, {
      method: 'PUT', body: JSON.stringify({ template: tpl }),
    });
    setMsg(r.ok ? `Saved ${NOTE_LANG_NAMES[lang] ?? lang}` : 'Save failed');
    if (r.ok) setSaved(p => ({ ...p, [lang]: tpl }));
    setBusy(null);
  };

  const duplicate = async () => {
    if (!window.confirm('Copy the EN template over ALL other languages? Existing language versions will be overwritten.')) return;
    setBusy('dup');
    const r = await authFetch(`${API}/note-templates/${webshop.id}/duplicate`, { method: 'POST', body: '{}' });
    const d = await r.json().catch(() => ({}));
    setMsg(r.ok ? `Copied EN to ${d.copied_to} languages` : (d.detail || 'Duplicate failed — save EN first'));
    if (r.ok) load();
    setBusy(null);
  };

  const preview = async () => {
    setBusy('preview');
    setMsg('');
    const r = await authFetch(`${API}/note-templates/${webshop.id}/preview`, {
      method: 'POST', body: JSON.stringify({ language: lang, template: tpl }),
    });
    if (r.ok) {
      const blob = await r.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } else {
      const d = await r.json().catch(() => ({}));
      setMsg((d as any).detail || 'Preview failed');
    }
    setBusy(null);
  };

  const F = (key: string, label: string, multiline?: boolean) => (
    <div key={key}>
      <label className="text-gray-500 text-xs">{label}</label>
      {multiline ? (
        <textarea value={tpl[key] ?? ''} rows={4}
          onChange={e => setTpl((p: any) => ({ ...p, [key]: e.target.value }))}
          className="mt-0.5 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
      ) : (
        <input value={tpl[key] ?? ''}
          onChange={e => setTpl((p: any) => ({ ...p, [key]: e.target.value }))}
          className="mt-0.5 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
      )}
    </div>
  );

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={lang} onChange={e => pickLang(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2">
          {langs.map(lg => (
            <option key={lg} value={lg}>
              {(NOTE_LANG_NAMES[lg] ?? lg)}{saved[lg] ? ' ✓' : ''}
            </option>
          ))}
        </select>
        <button onClick={save} disabled={busy !== null}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
          {busy === 'save' ? 'Saving…' : `Save ${lang.toUpperCase()}`}
        </button>
        <button onClick={preview} disabled={busy !== null}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
          {busy === 'preview' ? 'Rendering…' : 'Preview PDF'}
        </button>
        <button onClick={duplicate} disabled={busy !== null || !saved['en']}
          title={saved['en'] ? '' : 'Save the EN version first'}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white text-sm font-medium rounded-lg">
          {busy === 'dup' ? 'Copying…' : 'Copy EN → all languages'}
        </button>
        {msg && <span className="text-sm text-green-400">{msg}</span>}
      </div>
      <p className="text-gray-500 text-xs">
        The note prints in the customer&apos;s language automatically (order language → this template; falls back to EN).
        Workflow: write the EN version → Copy EN → all languages → refine individual languages over time.
        Languages marked ✓ have a saved version.
      </p>

      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-1">Store logo on the note</div>
        <p className="text-gray-500 text-xs mb-3">Printed top-left on every delivery note (all languages). PNG with transparency works best, max 300 KB. Without a logo the store name is printed as text.</p>
        <div className="flex items-center gap-4 flex-wrap">
          {storeLogo
            ? <img src={storeLogo} alt="store logo" className="h-10 max-w-[220px] object-contain bg-white rounded-lg p-1.5" />
            : <span className="text-gray-600 text-sm">No logo — “{webshop.name}” is printed as text</span>}
          <label className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg cursor-pointer">
            Upload logo
            <input type="file" accept="image/*" className="hidden"
              onChange={e => uploadStoreLogo(e.target.files?.[0] ?? null)} />
          </label>
          {storeLogo && (
            <button onClick={() => saveStoreLogo(null)} className="px-4 py-2 text-gray-500 hover:text-red-400 text-sm">
              Remove
            </button>
          )}
          {logoMsg && <span className="text-green-400 text-sm">{logoMsg}</span>}
        </div>
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={!!tpl.show_return_column}
            onChange={e => setTpl((p: any) => ({ ...p, show_return_column: e.target.checked }))} />
          Show &quot;return qty&quot; column
        </label>
        <label className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={!!tpl.show_location}
            onChange={e => setTpl((p: any) => ({ ...p, show_location: e.target.checked }))} />
          Show warehouse location on items
        </label>
      </div>

      {NOTE_FIELD_SECTIONS.map(sec => (
        <div key={sec.title} className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
          <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3">{sec.title}</div>
          <div className="grid grid-cols-2 gap-4">
            {sec.fields.map(f => F(f.key, f.label, f.multiline))}
          </div>
        </div>
      ))}
    </div>
  );
}

function StoreShippingPage({ webshop }: { webshop: Webshop }) {
  const [tab, setTab] = useState<'general'|'senders'|'carriers'|'codes'|'methods'|'pickups'|'note'|'storefront'|'services'>('general');
  // One mental model on every platform: the Shipping Methods tab is where a
  // store's ways-of-shipping live. MCF imports them from the platform; Shopify
  // defines them here (the unified methods editor replaces the old
  // "Storefront Offering" tab).
  const TABS = [
    { key:'general',  label:'General'          },
    { key:'senders',  label:'Senders'          },
    { key:'carriers', label:'Carriers'          },
    { key:'codes',    label:'Service Codes'     },
    { key:'methods',  label:'Shipping Methods'  },
    { key:'services', label:'Carrier Services'   },
    { key:'pickups',  label:'Pickup Point Lists' },
    { key:'note',     label:'Delivery Note'      },
  ] as const;
  return (
    <div>
      <div className="flex gap-1 mb-6 border-b border-gray-800 pb-3">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${tab===t.key ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-white'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab==='general'  && <ShippingGeneralTab webshop={webshop} />}
      {tab==='senders'  && <SendersTab         webshop_id={webshop.id} />}
      {tab==='carriers' && <CarriersTab        webshop_id={webshop.id} />}
      {tab==='codes'    && <ServiceCodesTab    webshop_id={webshop.id} />}
      {tab==='methods'  && (webshop.platform === 'shopify'
        ? <StorefrontTab webshop_id={webshop.id} />
        : <ShippingMethodsTab webshop_id={webshop.id} />)}
      {tab==='services' && <CarrierServicesTab webshop_id={webshop.id} />}
      {tab==='pickups'  && <PickupPointsTab webshop_id={webshop.id} />}
      {tab==='note'     && <NoteTemplateTab webshop={webshop} />}
    </div>
  );
}

function OrgServiceCodes({ carrierCode }: { carrierCode: string }) {
  const [codes, setCodes] = useState<ServiceCode[]>([]);
  const [newCode, setNewCode] = useState({ code: '', name: '' });
  const load = useCallback(async () => {
    const r = await authFetch(`${API}/shipping/org-service-codes?carrier_code=${encodeURIComponent(carrierCode)}`);
    if (r.ok) setCodes(await r.json());
  }, [carrierCode]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (id: number, active: boolean) => {
    await authFetch(`${API}/shipping/service-codes/${id}`, { method: 'PATCH', body: JSON.stringify({ is_active: active }) });
    load();
  };
  const add = async () => {
    if (!newCode.code) return;
    await authFetch(`${API}/shipping/service-codes`, { method: 'POST', body: JSON.stringify({ carrier_code: carrierCode, ...newCode }) });
    setNewCode({ code: '', name: '' });
    load();
  };
  return (
    <div className="mt-2 ml-2 border-l-2 border-gray-800 pl-3 space-y-1">
      {codes.map(sc => (
        <label key={sc.id} className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={sc.is_active} onChange={e => toggle(sc.id, e.target.checked)}
            className="rounded border-gray-600 bg-gray-700 accent-blue-500" />
          <span className="font-mono text-gray-300 w-20">{sc.code}</span>
          <span className={sc.is_active ? 'text-white' : 'text-gray-500'}>{sc.name}</span>
        </label>
      ))}
      <div className="flex gap-2 pt-1">
        <input value={newCode.code} placeholder="code" onChange={e => setNewCode(p => ({ ...p, code: e.target.value }))}
          className="bg-gray-900 border border-gray-800 rounded px-2 py-1 text-xs text-white w-24 font-mono" />
        <input value={newCode.name} placeholder="name" onChange={e => setNewCode(p => ({ ...p, name: e.target.value }))}
          className="bg-gray-900 border border-gray-800 rounded px-2 py-1 text-xs text-white w-56" />
        <button onClick={add} className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded">+ Add</button>
      </div>
    </div>
  );
}

function OrgCarriersPanel() {
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any|null>(null);
  const [codesFor, setCodesFor] = useState<number | null>(null);
  const [form, setForm] = useState<any>({ carrier_code: 'dpd_baltics', label: '', credentials: {} });
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    const r = await authFetch(`${API}/shipping/org-carriers`);
    if (r.ok) setRows(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);
  const startNew  = () => { setEditing(null); setForm({ carrier_code: 'dpd_baltics', label: '', credentials: {} }); setOpen(true); };
  const startEdit = (c: any) => { setEditing(c); setForm({ carrier_code: c.carrier_code, label: c.label, is_active: c.is_active, credentials: { ...(c.credentials_masked ?? {}) } }); setOpen(true); };
  const save = async () => {
    setError('');
    const r = editing
      ? await authFetch(`${API}/shipping/org-carriers/${editing.id}`, { method: 'PUT',  body: JSON.stringify(form) })
      : await authFetch(`${API}/shipping/org-carriers`,               { method: 'POST', body: JSON.stringify(form) });
    if (r.ok) { setOpen(false); setEditing(null); load(); }
    else { const d = await r.json().catch(() => ({})); setError((d as any).detail || 'Save failed'); }
  };
  const del = async (c: any) => {
    if (!window.confirm(`Delete WMS contract "${c.label}"?`)) return;
    const r = await authFetch(`${API}/shipping/org-carriers/${c.id}`, { method: 'DELETE' });
    if (!r.ok) { const d = await r.json().catch(() => ({})); setError((d as any).detail || 'Delete failed'); }
    load();
  };
  const credFields = CARRIER_CRED_FIELDS[form.carrier_code] ?? [];
  return (
    <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <p className="text-white font-semibold text-sm">WMS carrier contracts (shared)</p>
        <span className="text-gray-600 text-xs">— credentials stored once; stores link to these. Editing here updates every linked store.</span>
      </div>
      <Err msg={error} />
      <div className="space-y-2 mt-3">
        {rows.map(c => (
          <div key={c.id} className="bg-gray-900/50 border border-gray-800 rounded-lg px-3 py-2">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <span className="text-white text-sm font-medium">{c.label}</span>
                <span className="text-gray-500 text-xs ml-2">{CARRIER_CODES.find(x => x.value===c.carrier_code)?.label ?? c.carrier_code}</span>
              </div>
              <span className="text-xs text-gray-500">{c.linked_stores} linked store{c.linked_stores === 1 ? '' : 's'}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${c.is_active ? 'bg-green-900/40 text-green-400' : 'bg-gray-800 text-gray-500'}`}>
                {c.is_active ? 'Active' : 'Inactive'}
              </span>
              <button onClick={() => setCodesFor(codesFor === c.id ? null : c.id)}
                className={`px-2 py-1 text-xs rounded-lg ${codesFor === c.id ? 'bg-blue-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}>
                Service codes
              </button>
              <button onClick={() => startEdit(c)} className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg">Edit</button>
              <button onClick={() => del(c)} className="px-2 py-1 text-xs text-gray-600 hover:text-red-400 rounded-lg">✕</button>
            </div>
            {codesFor === c.id && <OrgServiceCodes carrierCode={c.carrier_code} />}
          </div>
        ))}
      </div>
      {open ? (
        <div className="mt-3 space-y-3 border-t border-gray-800 pt-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Carrier type</label>
              <select value={form.carrier_code} disabled={!!editing}
                onChange={e => setForm((p: any) => ({ ...p, carrier_code: e.target.value, credentials: {} }))}
                className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none disabled:opacity-60">
                {CARRIER_CODES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            {/* Name a contract after the CARRIER, never after a store: the WMS holds
                these on behalf of every store, and the holder is its own entity. */}
            <Inp label="Contract label (e.g. DPD Baltics)" value={form.label} onChange={v => setForm((p: any) => ({ ...p, label: v }))} />
          </div>
          {credFields.map(cf => (
            <Inp key={cf.key} label={cf.label} secret={cf.secret}
              value={form.credentials?.[cf.key] ?? ''}
              onChange={v => setForm((p: any) => ({ ...p, credentials: { ...p.credentials, [cf.key]: v } }))} />
          ))}
          {form.carrier_code === 'posti' && editing && <PostiTrackingTest />}
          <div className="flex gap-2">
            <button onClick={save} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">Save</button>
            <button onClick={() => { setOpen(false); setEditing(null); }} className="px-4 py-2 bg-gray-700 text-gray-300 text-sm rounded-lg">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={startNew} className="mt-3 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded-lg">+ New WMS contract</button>
      )}
    </div>
  );
}

// Parcel tracking thresholds (parcel tracking v2, 2026-10-02) — one org-wide setting, used by Statistics →
// Parcels (the Exceptions reasons, the dashboard's "% within expectation") and by the connector. Every number
// here changes what the warehouse is shown; nothing else hides a threshold.
function TrackingSettingsPanel() {
  const [st, setSt] = useState<any | null>(null);
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    authFetch(`${API}/tracking/settings`).then(r => r.ok ? r.json() : null).then(setSt).catch(() => {});
  }, []);
  if (!st) return null;
  const setMap = (key: string, carrier: string, v: string) =>
    setSt((s: any) => ({ ...s, [key]: { ...(s[key] || {}), [carrier]: v === '' ? '' : Number(v) } }));
  const save = async () => {
    setErr(''); setSaved('');
    const body = {
      pickup_default_days: Number(st.pickup_default_days), stuck_days_default: Number(st.stuck_days_default),
      stuck_days: Object.fromEntries(CARRIER_CODES.map(c => [c.value, Number(st.stuck_days?.[c.value] ?? st.stuck_days_default)])),
      first_scan_workdays: Object.fromEntries(CARRIER_CODES.map(c => [c.value, Number(st.first_scan_workdays?.[c.value] ?? 2)])),
    };
    const r = await authFetch(`${API}/tracking/settings`, { method: 'PUT', body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setSt(d); setSaved('Saved');
  };
  const num = 'w-16 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white text-right';
  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-4 mb-6">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-left">
        <span>
          <span className="text-white font-medium">Parcel tracking</span>
          <span className="text-gray-500 text-sm ml-2">when a parcel becomes an exception · the first-scan expectation</span>
        </span>
        <span className="text-gray-500 text-sm">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-4 text-sm">
          <div className="overflow-x-auto">
            <table className="text-sm">
              <thead><tr className="text-gray-500 text-xs">
                <th className="text-left pr-6 py-1">Carrier</th>
                <th className="text-left px-3" title="Workdays after processing within which the carrier's first scan is expected">First scan expected within<br/><span className="font-normal">workdays after processing</span></th>
                <th className="text-left px-3" title="A moving parcel with no new event for this many days is an exception">No movement for<br/><span className="font-normal">days → exception</span></th>
              </tr></thead>
              <tbody>
                {CARRIER_CODES.map(c => (
                  <tr key={c.value} className="border-t border-gray-800/60">
                    <td className="pr-6 py-1.5 text-white">{c.pill}</td>
                    <td className="px-3"><input type="number" min={0} max={15} className={num}
                      value={st.first_scan_workdays?.[c.value] ?? ''} onChange={e => setMap('first_scan_workdays', c.value, e.target.value)} /></td>
                    <td className="px-3"><input type="number" min={1} max={30} className={num}
                      value={st.stuck_days?.[c.value] ?? st.stuck_days_default} onChange={e => setMap('stuck_days', c.value, e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <label className="flex items-center gap-2 text-gray-300">
            Waiting at a pickup point without a carrier deadline: an exception after
            <input type="number" min={2} max={30} className={num} value={st.pickup_default_days}
              onChange={e => setSt((s: any) => ({ ...s, pickup_default_days: e.target.value }))} />
            − 2 days
          </label>
          <p className="text-gray-500 text-xs">
            Workdays = Monday–Friday that are not a public holiday in Estonia or the customer's country. Bring's first scan
            is at its terminal in Norway, Sweden or Denmark, after the HRX truck — so its expectation is longer.
          </p>
          <div className="flex items-center gap-3">
            <button onClick={save} className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm">Save</button>
            {saved && <span className="text-green-400 text-xs">{saved}</span>}
            {err && <span className="text-red-400 text-xs">{err}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ShippingSettings() {
  const [webshops, setWebshops] = useState<Webshop[]>([]);
  const [selected, setSelected] = useState('');
  useEffect(() => {
    authFetch(`${API}/settings/webshops`).then(r => r.ok ? r.json() : []).then((ws: Webshop[]) => {
      setWebshops(ws); if (ws.length>0) setSelected(ws[0].id);
    });
  }, []);
  const current = webshops.find(w => w.id===selected);
  return (
    <div className="max-w-4xl">
      <p className="text-gray-500 text-sm mb-6">Configure carriers, senders, and shipping method mappings per store.</p>
      <OrgCarriersPanel />
      <TrackingSettingsPanel />
      {webshops.length>1 && (
        <div className="flex gap-1 mb-6 border-b border-gray-800 pb-3">
          {webshops.map(w => (
            <button key={w.id} onClick={() => setSelected(w.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selected===w.id ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'}`}>
              {w.name}
            </button>
          ))}
        </div>
      )}
      {current ? <StoreShippingPage key={current.id} webshop={current} /> : <p className="text-gray-600 text-sm">No stores configured.</p>}
    </div>
  );
}

// Posti Tracking API — the one-shot access test (2026-09-30). ONE sign-in and, if it works, ONE lookup
// of our newest Posti parcel; after a refused sign-in the next try waits an hour, because Posti locks
// the account after 5 refused sign-ins. Save the account name and secret first. Claude, 2026-09-30.
function PostiTrackingTest() {
  const [res, setRes] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    authFetch(`${API}/tracking/posti/test`).then(r => r.ok ? r.json() : null).then(setRes).catch(() => {});
  }, []);
  const run = async () => {
    if (!window.confirm('Sign in to Posti ONCE with the saved Tracking API account and look up one parcel?\n\nSave the account name and secret first. After a refused sign-in the next try is allowed in an hour.')) return;
    setBusy(true); setErr('');
    const r = await authFetch(`${API}/tracking/posti/test`, { method: 'POST', body: '{}' });
    const d = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setRes({ last: d });
  };
  const last = res?.last;
  const report = last ? postiReport(last) : '';
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-3 text-xs">
      <div className="flex items-center gap-3">
        <span className="text-white font-medium text-sm">Posti tracking</span>
        <button onClick={run} disabled={busy} className="px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white">
          {busy ? 'Testing…' : 'Test Posti tracking (one sign-in)'}</button>
        {res?.hold_minutes_left ? <span className="text-amber-300">next attempt in {res.hold_minutes_left} min</span> : null}
        {report && <button onClick={() => { try { navigator.clipboard.writeText(report); } catch { /* */ } }}
          className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-white">Copy report</button>}
      </div>
      {err && <div className="text-red-300 mt-2">{err}</div>}
      {last && (
        <pre className="mt-3 whitespace-pre-wrap break-words font-mono text-[12px] leading-relaxed text-gray-200 bg-black/40 border border-gray-800 rounded p-3">{report}</pre>
      )}
    </div>
  );
}

/** The test as a plain report for Posti support (no secret, no token). */
function postiReport(l: any): string {
  const out: string[] = [];
  out.push('Posti Tracking API test — Motley Denim Europe OÜ (own WMS integration)');
  out.push(`Time: ${String(l.tested_at).replace('T', ' ')} UTC`);
  out.push(`Service account: ${l.account}${l.contract ? `   ·   Posti contract / customer number on our labels: ${l.contract}` : ''}`);
  out.push('');
  const si = l.sign_in || {};
  out.push('1) Sign-in');
  out.push(`   Request:  ${si.request || 'POST https://oauth2.posti.com/oauth/token?grant_type=client_credentials'}`);
  out.push(`   Auth:     ${si.auth || 'HTTP Basic'}`);
  out.push(`   Result:   HTTP ${si.status ?? '—'} — ${si.ok ? 'OK, access token received' : 'refused'}${si.detail && !si.ok ? ` (${si.detail})` : ''}`);
  const ti = si.token_info;
  if (ti) {
    const { token_claims, ...rest } = ti;
    const shown = Object.entries(rest).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ');
    if (shown) out.push(`   Token:    ${shown}`);
    if (token_claims && typeof token_claims === 'object') out.push(`   Claims:   ${JSON.stringify(token_claims)}`);
  }
  const looks: any[] = l.lookups || (l.lookup ? [l.lookup] : []);
  looks.forEach((x, i) => {
    out.push('');
    out.push(`${i + 2}) Tracking lookup — our parcel ${x.tracking_number}${x.sent_to_posti ? ` (label made ${x.sent_to_posti})` : ''}`);
    out.push(`   Request:  ${x.request || `GET https://api.posti.fi/tracking/7/shipments/trackingnumbers/${x.tracking_number}`}`);
    out.push(`   Auth:     ${x.auth || 'Authorization: Bearer <token from step 1>'}`);
    out.push(`   Result:   HTTP ${x.status}${x.status === 200 ? ` — ${x.shipments} shipment(s), ${x.events} event(s)` : x.status === 403 ? ' Forbidden' : ''}`);
    if (x.status !== 200) {
      out.push(`   Body:     ${x.response_body ?? x.detail ?? '(empty)'}`);
      const h = x.response_headers || {};
      const keep = Object.entries(h).filter(([k]) => !/^(date|content-length|connection|strict-transport-security)$/i.test(k));
      if (keep.length) out.push(`   Headers:  ${keep.map(([k, v]) => `${k}: ${v}`).join(' | ')}`);
    }
  });
  if (looks.length && looks.every(x => x.status === 403))
    out.push('', 'Conclusion: the account signs in, but every Tracking API call is refused with 403 Forbidden —',
                 'please add Tracking API access ("normal" access, our own shipments) to this service account.');
  return out.join('\n');
}
